import React from 'react';
import { Gavel, CheckCircle2, AlertCircle, FileStack, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0B0F1A] transition-colors duration-300">
      {/* Header section with back button */}
      <div className="border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#666666] dark:text-gray-400 hover:text-[#D0021B] dark:hover:text-[#FF4D4D] transition-colors font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Gavel className="w-6 h-6 text-[#D0021B]" />
            <h1 className="font-serif text-2xl font-bold dark:text-white">Terms of Service</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-12">
          {/* User Responsibilities */}
          <section className="bg-gray-50 dark:bg-gray-800/30 p-8 rounded-3xl border border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-[#D0021B]/10 rounded-2xl text-[#D0021B]">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-serif font-bold dark:text-white">Authorized Usage</h2>
            </div>
            <div className="space-y-4 text-[#666666] dark:text-gray-400">
              <p>
                This system is reserved exclusively for authorized administrative personnel and registered 
                press staff. Users are strictly prohibited from:
              </p>
              <ul className="grid md:grid-cols-2 gap-4 text-sm font-medium">
                <li className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                  Forging or tampering with Press ID card data.
                </li>
                <li className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                  Sharing administrative login credentials.
                </li>
                <li className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                  Using the system to harass or impersonate news staff.
                </li>
                <li className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                  Attempting to bypass security or auditing protocols.
                </li>
              </ul>
            </div>
          </section>

          {/* Account Security */}
          <section>
            <h2 className="text-xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-4">Account & Password Security</h2>
            <p className="text-[#666666] dark:text-gray-400 leading-relaxed mb-4">
              You are responsible for maintaining the confidentiality of your account credentials. 
              Any activity performed under your login will be attributed to you. We recommend using 
              strong, unique passwords and changing them periodically.
            </p>
            <div className="flex items-start gap-4 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-2xl">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                If you suspect unauthorized access to your account, you must notify the IT department 
                immediately via the <Link to="/support" className="underline font-bold">Support Portal</Link>.
              </p>
            </div>
          </section>

          {/* Data Accuracy */}
          <section>
            <h2 className="text-xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-4">Data Integrity</h2>
            <p className="text-[#666666] dark:text-gray-400 leading-relaxed">
              Administrators must ensure that all staff names, mobile numbers, and designations entered 
              into the system match official employment records. Providing false information for 
              ID card generation is grounds for immediate termination of system access.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-4">Termination of Access</h2>
            <p className="text-[#666666] dark:text-gray-400 leading-relaxed">
              We reserve the right to suspend or terminate account access at any time for violation of 
              these terms or protocol breaches. Upon termination of employment, all system access 
              will be revoked as part of the standard offboarding procedure.
            </p>
          </section>

          {/* Governing Law */}
          <section className="bg-gray-50 dark:bg-gray-800/30 p-8 rounded-3xl border border-gray-100 dark:border-gray-800">
            <h2 className="text-xl font-serif font-bold dark:text-white mb-4">Governing Law</h2>
            <p className="text-[#666666] dark:text-gray-400 text-sm">
              These terms are governed by the internal news organization policy and applicable digital 
              identity laws. Any disputes will be handled through the organizational ethics and 
              legal compliance departments.
            </p>
          </section>

          {/* Footer of doc */}
          <section className="text-center pt-8 border-t border-gray-100 dark:border-gray-800">
            <p className="text-sm text-[#999999]">Last Updated: April 2026</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
